import React from 'react';
import ReactDOM from 'react-dom';
import ReactHintFactory from 'react-hint';
import { useReduxState } from 'hooks';
import { Badge, ProgressBar } from 'components/common';

import type { ChapterProgress } from 'shared-types';

const ReactHint = ReactHintFactory(React);

interface Props {
  chapterId: number;
  completionEnabled: boolean;
  progressData: ChapterProgress;
  requiredReadingProgress: number;
  requiredTasksProgress: number;
  requiredVideoProgress: number;
}

const CourseChaptersStatus = ({
  chapterId,
  completionEnabled,
  progressData,
  requiredReadingProgress,
  requiredVideoProgress
}: Props) => {
  const { messages, serverConfig, theme } = useReduxState();

  const {
    numberOfTasks,
    numberOfVideos,
    readingProgress,
    readingProgressMaxTime,
    taskAnswers,
    videoProgress,
    videoProgressVideosWatched
  } = progressData || {};

  let totalProgress = 0;
  let numberOfProgresses = 0;

  if (readingProgressMaxTime) {
    totalProgress += readingProgress;
    numberOfProgresses++;
  }

  if (numberOfTasks && taskAnswers !== undefined) {
    totalProgress += taskAnswers / numberOfTasks;
    numberOfProgresses++;
  }

  if (serverConfig.videoRequirementsType === 'individual') {
    if (numberOfVideos && videoProgressVideosWatched !== undefined) {
      totalProgress += videoProgressVideosWatched / numberOfVideos;
      numberOfProgresses++;
    }
  } else {
    if (numberOfVideos && videoProgress !== undefined) {
      totalProgress += videoProgress;
      numberOfProgresses++;
    }
  }

  if (numberOfProgresses) {
    totalProgress = totalProgress / numberOfProgresses;
  }

  const getRequiredLabel = (requiredProgress: number) => (
    <span
      style={{ color: theme.textColorLight, fontWeight: 400, marginLeft: 5 }}
    >
      {`(${requiredProgress}% ${messages.readerCompletionRequired})`}
    </span>
  );

  const getTooltipContent = () => (
    <div className="content">
      {readingProgressMaxTime ? (
        <div className="section">
          <div className="label">{messages.tasksReading}</div>
          <div className="value">
            <ProgressBar
              color={theme.textColorLight}
              label={
                <span>
                  {`${Math.floor(readingProgress * 100)}%`}
                  {completionEnabled
                    ? getRequiredLabel(requiredReadingProgress)
                    : null}
                </span>
              }
              progress={readingProgress}
              style={{ minWidth: 120 }}
            />
          </div>
        </div>
      ) : null}

      {numberOfVideos ? (
        <div className="section">
          <div className="label">{messages.tasksVideos}</div>
          <div className="value">
            <ProgressBar
              color={theme.textColorLight}
              label={
                <span>
                  {serverConfig.videoRequirementsType === 'individual'
                    ? `${videoProgressVideosWatched}/${numberOfVideos}`
                    : `${Math.floor(videoProgress * 100)}%`}
                  {serverConfig.videoRequirementsType !== 'individual' &&
                  completionEnabled
                    ? getRequiredLabel(requiredVideoProgress)
                    : null}
                </span>
              }
              progress={
                serverConfig.videoRequirementsType === 'individual'
                  ? videoProgressVideosWatched / numberOfVideos
                  : videoProgress
              }
              style={{ minWidth: 120 }}
            />
          </div>
        </div>
      ) : null}

      {numberOfTasks ? (
        <div className="section">
          <div className="label">{messages.tasksAnswers}</div>
          <div className="value">
            <ProgressBar
              color={theme.textColorLight}
              label={<span>{`${taskAnswers}/${numberOfTasks}`}</span>}
              progress={taskAnswers / numberOfTasks}
              style={{ minWidth: 120 }}
            />
          </div>
        </div>
      ) : null}

      <style jsx>{`
        .content {
          padding: 10px 5px 5px;
        }
        .section {
          display: table-row;
        }
        .label {
          display: table-cell;
          padding-right: 30px;
        }
        .value {
          display: table-cell;
        }
        .section:not(:first-child) .label,
        .section:not(:first-child) .value {
          padding-top: 10px;
        }
      `}</style>
    </div>
  );

  if (!progressData) {
    return null;
  }

  if (!readingProgressMaxTime && !numberOfVideos && !numberOfTasks) {
    return (
      <div style={{ color: theme.textColorLighter }}>
        {messages.commonNoContent}
      </div>
    );
  }

  return (
    <div className="progress">
      {progressData
        ? ReactDOM.createPortal(
            <ReactHint
              attribute={`data-chapter-progres-tooltip-${chapterId}`}
              autoPosition={true}
              className="vuo-tooltip-alt"
              events={true}
              delay={50}
              onRenderContent={() => (
                <div className="vuo-tooltip__content">
                  {getTooltipContent()}
                </div>
              )}
            />,
            document.querySelector('.dynamic-tooltip-root')
          )
        : null}

      <div
        aria-label={
          `${messages.tasksReading} ${readingProgress}` +
          (numberOfVideos ? `, ${messages.tasksVideos} ${videoProgress}` : '') +
          (numberOfTasks
            ? `, ${messages.commonTaskAnswers} ${taskAnswers}`
            : '')
        }
        className="icon"
        {...{ [`data-chapter-progres-tooltip-${chapterId}`]: true }}
      >
        <Badge
          isRounded={false}
          text={
            <div style={{ display: 'inline-flex', flexDirection: 'row' }}>
              <div>{`${Math.round(totalProgress * 100)}%`}</div>
              <ProgressBar
                color="#555"
                progress={totalProgress || 0}
                style={{ margin: '5px 0 5px 8px', width: 60 }}
              />
            </div>
          }
        />
      </div>

      <style jsx>{`
        .icon {
          color: inherit;
          font-size: 1.3rem;
          padding: 3px;
        }
      `}</style>
    </div>
  );
};

export default CourseChaptersStatus;
